import React from "react";
import axiosInstance from "../../api/axiosInstance";
import { backendUrl } from "../../api/constants";
import { useSnackbar } from "notistack";
import { modal } from "../../util/useModal";
import AppStatusScreen from "../AppStatusScreen/AppStatusScreen";
import KitButton from "../kit/Button";

const stripNameFromAddress = (name, address) => {
  if (!address) return "address unavailable";
  if (!name) return address;

  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const stripped = address
    .replace(new RegExp(`^${escaped}\\s*,\\s*`, "i"), "")
    .trim();

  return stripped || address;
};

const getTicketVenue = (ticket) =>
  ticket?.type === "claim" ? ticket?.existing_resource : ticket?.resource;

const formatTicketStatus = (status) =>
  status ? status.replaceAll("_", " ") : "pending";

const getStatusClass = (status) => {
  const normalized = status?.toLowerCase();

  if (normalized === "approved") {
    return "text-success";
  }

  if (normalized === "rejected") {
    return "text-danger";
  }

  return "text-warning";
};

const PendingPosts = ({ tickets, onTicketsChange, refreshControl = null }) => {
  const { enqueueSnackbar } = useSnackbar();
  const formattedTickets = (tickets || []).map((ticket) => {
    const venue = getTicketVenue(ticket);

    return {
      ...ticket,
      venue,
      name: venue?.name || "untitled venue",
      address: stripNameFromAddress(venue?.name, venue?.address),
      createdAt: ticket?.createdAt
        ? new Date(ticket.createdAt).toLocaleString()
        : "date unavailable",
      createdAtCompact: ticket?.createdAt
        ? new Date(ticket.createdAt).toLocaleString(undefined, {
            month: "numeric",
            day: "numeric",
            year: "2-digit",
            hour: "numeric",
            minute: "2-digit",
          })
        : "date unavailable",
      status: formatTicketStatus(ticket?.status),
    };
  });

  const handleDelete = async (ticket) => {
    const isPending = !["approved", "rejected"].includes(ticket.status?.toLowerCase());
    const confirmed = await modal(({ proceed }) => (
      <AppStatusScreen
        title={isPending ? "withdraw submission?" : "remove submission?"}
        subtitle={isPending ? `${ticket.name} won't be reviewed` : `${ticket.name} will be removed from this list`}
        actions={
          <div className="mx-auto grid max-w-[15.75rem] grid-cols-2 gap-kit-10">
            <KitButton fullWidth onClick={() => proceed(false)}>
              keep
            </KitButton>
            <KitButton variant="danger-strong" fullWidth onClick={() => proceed(true)}>
              {isPending ? "withdraw" : "remove"}
            </KitButton>
          </div>
        }
      />
    ), { fullScreen: true, raw: true });
    if (!confirmed) return;

    const previousTickets = tickets || [];
    onTicketsChange?.(previousTickets.filter((item) => item._id !== ticket._id));

    try {
      const { data } = await axiosInstance.post(`${backendUrl}/business/tickets/cancel/${ticket._id}`);
      if (data?.error) throw new Error(data.error);
    } catch (error) {
      console.log(error);
      onTicketsChange?.(previousTickets);
      enqueueSnackbar(typeof error?.message === "string" && error.message ? error.message : "could not remove submission", {
        variant: "error",
      });
    }
  };

  return (
    <div className="flex flex-col gap-kit-16 md:max-w-[36rem]">
      <div className="mt-kit-24 px-kit-16 text-kit-base font-['Primary-Medium'] text-[var(--inyc-primary-text-color)]">
        shows the current status of venue submissions you haven't claimed
      </div>
      <div className="-mt-[6px] px-kit-16">
        <div className="mb-kit-2 border-b-[1px] border-[color:color-mix(in_srgb,var(--inyc-outline-color)_35%,transparent)]" />
      </div>
      {refreshControl}

      {formattedTickets.length === 0 ? (
        <div className="rounded-kit-xl border-[1px] border-[var(--inyc-input-border-color)] bg-[var(--inyc-primary)] [:is(body.dark)_&]:bg-[var(--inyc-card-bg)] p-kit-32 text-center text-kit-base font-['Primary-Medium'] text-[var(--inyc-primary-text-color-light)]">
          no venue submissions
        </div>
      ) : (
        <div className="grid gap-kit-12">
          {formattedTickets.map((ticket) => (
            <article
              key={ticket._id}
              className="pending-venue-card w-full max-w-full overflow-hidden rounded-kit-xl border-[1px] border-[var(--inyc-input-border-color)] bg-[var(--inyc-primary)] p-kit-16 shadow-kit-card transition-all duration-200 hover:border-[var(--inyc-secondary)]"
            >
              <div className="flex min-w-0 items-stretch justify-between gap-kit-8">
                <div className="min-w-0">
                  <h3 className="m-0 text-kit-md font-['Primary-Bold'] leading-tight text-[var(--inyc-primary-text-color)]">
                    {ticket.name}
                  </h3>
                  <div className="mt-kit-6 text-kit-sm leading-relaxed text-[var(--inyc-primary-text-color)]">
                    {ticket.address}
                  </div>

                  <div className="mt-kit-8 flex min-w-0 flex-nowrap items-baseline gap-x-kit-12 text-kit-sm text-[var(--inyc-primary-text-color-light)] md:mt-kit-12 md:gap-x-kit-16">
                    <div className="flex min-w-0 items-baseline gap-kit-8 whitespace-nowrap">
                      <div className="shrink-0 font-['Primary-Medium'] text-[var(--inyc-primary-text-color)]">
                        created:
                      </div>
                      <div className="min-w-0 truncate text-kit-2xs uppercase tracking-[0.08em] opacity-60 md:overflow-visible md:whitespace-nowrap">
                        <span className="md:hidden">{ticket.createdAtCompact}</span>
                        <span className="hidden md:inline">{ticket.createdAt}</span>
                      </div>
                    </div>
                    <div className="flex shrink-0 items-baseline gap-kit-4 whitespace-nowrap">
                      <div className="font-['Primary-Medium'] text-[var(--inyc-primary-text-color)]">
                        request:
                      </div>
                      <div className="text-kit-2xs lowercase tracking-[0.08em] text-[var(--inyc-primary-text-color)]">
                        {ticket.type}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex w-[74px] shrink-0 flex-col items-center justify-between">
                  <span
                    className={`pending-venue-status-pill pending-venue-status-${ticket.status.replaceAll(" ", "-")} text-center text-kit-2xs font-['Primary-Medium'] leading-tight ${getStatusClass(
                      ticket.status
                    )}`}
                  >
                    {ticket.status}
                  </span>
                  <button
                    type="button"
                    className="pending-venue-delete-pill flex h-[28px] w-[74px] items-center justify-center rounded-kit-full border-[1px] text-kit-2xs font-['Primary-Medium']"
                    onClick={() => handleDelete(ticket)}
                  >
                    {["approved", "rejected"].includes(ticket.status?.toLowerCase()) ? "remove" : "withdraw"}
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
};

export default PendingPosts;
